/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import _sortBy from 'lodash.sortby';
import Editor, { ActionComposerEditorArgs } from 'cio/components/action-composer/base-glimmer/editor';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { gte } from '@ember/object/computed';
import { isNone, isEmpty } from '@ember/utils';
import { scheduleOnce } from '@ember/runloop';
import { EMAIL_COMPOSER_ROLLBACK } from 'cio/services/event-bus';
import EnvironmentService from 'cio/services/environment';
import AuthService from 'cio/services/auth';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { addNewHeader, removeHeader, updateHeaderName, updateHeaderValue } from 'cio/utils/models/header';
import { IdentityOption } from 'cio/utils/action-composer';
import { getLiquidToInsert } from 'cio/utils/liquid';
import CioAceEditor from 'cio/components/cio-ace-editor';
import CioWYSIWYGEditor from 'cio/components/cio-wysihtml5-editor';

interface TextAndCodeEditorComponentArgs extends ActionComposerEditorArgs {
  onPreviewEmail: () => unknown;
}

interface TextAndCodeEditorSignature {
  Args: TextAndCodeEditorComponentArgs;
  Element: HTMLDivElement;
}

export default class TextAndCodeEditor extends Editor<TextAndCodeEditorSignature> {
  @service environment!: EnvironmentService;
  @service auth!: AuthService;

  readonly defaultRecipient = '{{customer.email}}';

  @tracked isHeadersExpanded = true;
  @tracked isReplyToVisible = false;
  @tracked isBccVisible = false;

  @tracked isPreheaderTextDisplayed!: boolean;

  constructor(owner: unknown, args: TextAndCodeEditorSignature['Args']) {
    super(owner, args);
    this.isPreheaderTextDisplayed = !isEmpty(args.editableEntity.template?.preheaderText);
  }

  @computed('args.editableEntity.template.preheaderText', 'isPreheaderTextDisplayed')
  get isPreheaderTextVisible() {
    if (this.isPreheaderTextDisplayed) {
      return true;
    }

    return !isEmpty(this.args.editableEntity.template?.preheaderText);
  }

  @gte('args.editableEntity.template.headers.length', 10) hasMaximumHeaders!: boolean;

  @computed('args.editableEntity.isTransactionalMessage')
  get isTransactionalMessage() {
    return this.args.editableEntity.isTransactionalMessage;
  }

  @computed('args.errors.headers.[]')
  get headerErrors() {
    return this.args.errors?.headers ?? [];
  }

  get editorComponent() {
    return this.args.editorName === 'html' ? CioAceEditor : CioWYSIWYGEditor;
  }

  @computed('args.editableEntity.template.id')
  get template() {
    return this.args.editableEntity.template!;
  }

  @computed('args.editableEntity.template.headers.[]', 'isReplyToVisible', 'isBccVisible')
  get headerCount() {
    let count = this.template.headers.length || 0;

    if (this.isReplyToVisible) {
      count++;
    }

    if (this.isBccVisible) {
      count++;
    }

    return count;
  }

  @computed('environment.current.identities.@each.templateType')
  get identities() {
    return this.environment.current.identities.filter((item) => item.templateType === 'email');
  }

  // used to conditionally show "From" address dropdown or CTA to setup sender and verify a new identity
  @computed('identities.@each.verified')
  get hasSelectableIdentity() {
    return this.identities.some((identity) => identity.verified !== 'unverified');
  }

  // options formatted for use in fly-power-select dropdown
  @computed('identities.@each.{address,verified}')
  get identitiesOptions() {
    return _sortBy(
      this.identities.map((identity): IdentityOption => {
        let domain = this.environment.domainForIdentity(identity);
        let unverified = identity.verified === 'unverified';
        let unavailableForNonTransactional = !this.isTransactionalMessage && domain?.isUsingTransactionalIpPool;

        return {
          domain,
          id: identity.id,
          address: identity.address,
          verified: identity.verified,
          emailDomain: identity.emailDomain,
          unverified,
          unavailableForNonTransactional,
          displayIpPool: this.isTransactionalMessage,
          disabled: unverified || unavailableForNonTransactional,
        };
      }),
      ['disabled'],
    ); // show disabled sender identities as last in the list
  }

  // options without "disabled" property: and so selectable in the address dropdown
  // this is used when choosing "Reply-to" identity, which doesn't have to be verified
  @computed('identitiesOptions')
  get identitiesOptionsAllEnabled() {
    return this.identitiesOptions.map((i) => ({ ...i, disabled: false }));
  }

  @action
  toggleReplyToAndBcc() {
    if (!isNone(this.template.get('replyToIdentity'))) {
      this.isReplyToVisible = true;
    }

    if (!isNone(this.template.get('bcc'))) {
      this.isBccVisible = true;
    }
  }

  @action
  subscribeToRollback() {
    this.eventBus.subscribe(EMAIL_COMPOSER_ROLLBACK, this, this._onEmailComposerRollback);
  }

  @action
  unsubscribeFromRollback() {
    this.eventBus.unsubscribe(EMAIL_COMPOSER_ROLLBACK, this, this._onEmailComposerRollback);
  }

  @action
  insertLiquid([fieldName, index]: [string, number], liquid: string, fallback?: string) {
    let fieldNameToSelector: Record<string, string> = {
      'template.bcc': '.js-editor-field-bcc input',
      'template.subject': '.js-editor-field-subject input',
      'template.preheaderText': '.js-editor-field-preheader input',
      'template.to': '.js-editor-field-to input',
      'header.name': '.js-editor-field-header-name input',
      'header.value': '.js-editor-field-header-value input',
    };

    let selector = fieldNameToSelector[fieldName];

    if (!selector) {
      return;
    }

    let el = document.querySelectorAll(selector)[index] as HTMLInputElement;
    let { selectionStart, selectionEnd } = el;

    let replacement = getLiquidToInsert(liquid, fallback, this.template.templateEngine);

    el.setRangeText(replacement, selectionStart ?? 0, selectionEnd ?? 0, 'end');
    if (el !== document.activeElement) {
      el.focus();
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }

  _onEmailComposerRollback() {
    if (isNone(this.template.replyToIdentity)) {
      this.isReplyToVisible = false;
    }

    if (isNone(this.template.bcc)) {
      this.isBccVisible = false;
    }
  }

  @action
  onFromIdentitySelect(identityOption: TextAndCodeEditor['identitiesOptions'][number]) {
    this.template.set(
      'fromIdentity',
      this.identities.find((item) => item.id === identityOption.id),
    );
  }

  @action
  onReplyToIdentitySelect(identityOption: TextAndCodeEditor['identitiesOptions'][number]) {
    this.template.set(
      'replyToIdentity',
      this.identities.find((item) => item.id === identityOption.id),
    );
  }

  @action
  changeRecipient() {
    this.template.set('recipient', '');

    scheduleOnce('afterRender', this, () => {
      (document.querySelector('.js-editor-field-to .fly-form-control') as HTMLInputElement)?.focus();
    });
  }

  @action
  useDefaultRecipient() {
    this.template.set('recipient', null);
  }

  @action
  showPreheaderText() {
    this.isPreheaderTextDisplayed = true;

    scheduleOnce('afterRender', this, () => {
      (document.querySelector('.js-preheader-text-input') as HTMLInputElement)?.focus();
    });
  }

  @action
  removePreheaderText() {
    this.isPreheaderTextDisplayed = false;
    this.template.set('preheaderText', null);
  }

  @action
  clearReplyTo() {
    this.isReplyToVisible = false;
    this.template.set('replyToIdentity', null);
  }

  @action
  showReplyTo() {
    this.template.set('replyToIdentity', this.template.get('fromIdentity'));

    this.isReplyToVisible = true;
    this.isHeadersExpanded = true;

    scheduleOnce('afterRender', this, () => {
      (document.querySelector('.js-editor-field-reply-to .fly-form-control') as HTMLInputElement)?.focus();
    });
  }

  @action
  showBcc() {
    this.isBccVisible = true;
    this.isHeadersExpanded = true;

    scheduleOnce('afterRender', this, () => {
      (document.querySelector('.js-editor-field-bcc .fly-form-control') as HTMLInputElement)?.focus();
    });
  }

  @action
  removeBcc() {
    this.isBccVisible = false;
    this.template.set('bcc', null);
  }

  @action
  switchEditor(editor: string) {
    this.template.set('editor', editor);

    if (editor !== 'html') {
      this.template.set('disablePreprocessor', false);
    }
  }

  @action
  addHeader() {
    this.template.headers = addNewHeader(this.template.headers);
    this.isHeadersExpanded = true;
  }

  @action
  removeHeader(index: number) {
    this.template.headers = removeHeader(this.template.headers, index);
  }

  @action
  updateHeaderName(index: number, event: FlyKeyboardEvent<HTMLInputElement>) {
    this.template.headers = updateHeaderName(this.template.headers, index, event.target.value);
  }

  @action
  updateHeaderValue(index: number, event: FlyKeyboardEvent<HTMLInputElement>) {
    this.template.headers = updateHeaderValue(this.template.headers, index, event.target.value);
  }

  @action
  toggleHeadersExpanded() {
    this.isHeadersExpanded = !this.isHeadersExpanded;
  }

  @action
  setTemplateFakeBcc() {
    if (!this.template.hasBcc) {
      this.template.set('fakeBcc', true);
    }
  }

  @action
  handlePreprocessorChange() {
    this.template.set('disablePreprocessor', !this.template.get('disablePreprocessor'));
    this.args.onPreviewEmail();
  }
}
